import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Shield, UserMinus, Ban, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";

interface GroupMember {
  user_id: string;
  joined_at: string;
  profiles: {
    username: string;
    avatar_url: string;
  };
}

interface GroupMembersProps {
  groupId: string;
  isAdmin: boolean;
}

export const GroupMembers = ({ groupId, isAdmin }: GroupMembersProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedMember, setSelectedMember] = useState<GroupMember | null>(null);
  const [moderationReason, setModerationReason] = useState("");
  const [isModerateDialogOpen, setIsModerateDialogOpen] = useState(false);

  const { data: groupInfo, isLoading: loadingGroupInfo } = useQuery({
    queryKey: ['group-info', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('groups')
        .select('created_by')
        .eq('id', groupId)
        .single();
        
      if (error) throw error;
      return data;
    }
  });

  const { data: members, isLoading } = useQuery({
    queryKey: ['group-members', groupId],
    queryFn: async () => {
      console.log('Fetching members for group:', groupId);
      
      // First fetch the member IDs from group_members
      const { data: memberData, error: memberError } = await supabase
        .from('group_members')
        .select('user_id, joined_at')
        .eq('group_id', groupId);
        
      if (memberError) {
        console.error('Error fetching group member IDs:', memberError);
        throw memberError;
      }
      
      console.log('Raw group member IDs:', memberData);
      
      if (!memberData || memberData.length === 0) {
        console.log('No members found for this group');
        return [];
      }
      
      // Then get profile information for each member
      const memberIds = memberData.map(m => m.user_id);
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .in('id', memberIds);
        
      if (profileError) {
        console.error('Error fetching member profiles:', profileError);
        throw profileError;
      }
      
      // Combine the data
      const processedData = memberData.map(member => {
        const profile = profileData?.find(p => p.id === member.user_id);
        return {
          user_id: member.user_id,
          joined_at: member.joined_at,
          profiles: {
            username: profile?.username || 'Unknown user',
            avatar_url: profile?.avatar_url || ''
          }
        };
      });
      
      return processedData as GroupMember[];
    },
    refetchOnMount: true,
  });

  const { data: creatorProfile, isLoading: loadingCreator } = useQuery({
    queryKey: ['group-creator', groupInfo?.created_by],
    enabled: !!groupInfo?.created_by && (!members || members.length === 0),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .eq('id', groupInfo?.created_by)
        .single();
        
      if (error) throw error;
      return {
        user_id: data.id,
        joined_at: new Date().toISOString(),
        profiles: {
          username: data.username,
          avatar_url: data.avatar_url
        }
      } as GroupMember;
    }
  });

  const handleRemoveMember = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', userId);

      if (error) throw error;

      // Update member count on removal
      const { data: groupData } = await supabase
        .from('groups')
        .select('members_count')
        .eq('id', groupId)
        .single();
        
      if (groupData && groupData.members_count > 0) {
        await supabase
          .from('groups')
          .update({ members_count: groupData.members_count - 1 })
          .eq('id', groupId);
      }

      queryClient.invalidateQueries({ queryKey: ['group-members', groupId] });

      toast({
        title: "Member removed",
        description: "The member has been removed from the group.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleModerateUser = async (actionType: 'mute' | 'ban') => {
    if (!selectedMember) return;

    try {
      const { error } = await supabase
        .from('group_moderation_actions')
        .insert({
          group_id: groupId,
          user_id: selectedMember.user_id,
          action_type: actionType,
          reason: moderationReason,
          expires_at: actionType === 'mute' ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() : null
        });

      if (error) throw error;

      setIsModerateDialogOpen(false);
      setSelectedMember(null);
      setModerationReason("");

      toast({
        title: `User ${actionType}ed`,
        description: `The user has been ${actionType}ed from the group.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (isLoading || loadingGroupInfo || loadingCreator) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  // Combine members data with creator data if needed
  const allMembers = [...(members || [])];
  
  // If we have creator info but creator is not in the members list, add them
  if (creatorProfile && !allMembers.some(m => m.user_id === creatorProfile.user_id)) {
    allMembers.push(creatorProfile);
  }

  if (allMembers.length === 0) {
    // This should rarely happen since the creator should always be a member
    return (
      <div className="text-center py-8 text-muted-foreground">
        No members found. This is unusual - please refresh the page.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {allMembers.map((member) => (
        <div key={member.user_id} className="flex items-center gap-4">
          <Avatar className="w-10 h-10">
            <img 
              src={member.profiles?.avatar_url} 
              alt={member.profiles?.username || 'User'}
              onError={(e) => {
                e.currentTarget.src = "https://images.unsplash.com/photo-1649972904349-6e44c42644a7";
              }}
            />
          </Avatar>
          <div>
            <p className="font-medium">{member.profiles?.username || 'Unknown user'}</p>
            <p className="text-sm text-muted-foreground">
              Joined {new Date(member.joined_at).toLocaleDateString()}
            </p>
          </div>
          {isAdmin && member.user_id !== groupInfo?.created_by && (
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleRemoveMember(member.user_id)}
              >
                <UserMinus className="h-4 w-4" />
              </Button>
              <Dialog open={isModerateDialogOpen} onOpenChange={setIsModerateDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setSelectedMember(member)}
                  >
                    <Shield className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Moderate User</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Textarea
                      placeholder="Reason for moderation action..."
                      value={moderationReason}
                      onChange={(e) => setModerationReason(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => handleModerateUser('mute')}
                        className="flex-1"
                      >
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Mute (7 days)
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => handleModerateUser('ban')}
                        className="flex-1"
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        Ban
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
